import { cn } from "@/lib/utils";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { normalizeAddress } from "@/lib/utils/address";
import { useMemo, useState } from "react";

interface TokenIconProps {
  logoUrl?: string | null;
  symbol: string;
  chainId?: number;
  tokenAddress?: string;
  size?: number;
  className?: string;
  enableStockTickers?: boolean;
}

export function TokenIcon({
  logoUrl,
  symbol,
  chainId,
  tokenAddress,
  size = 32,
  className,
  enableStockTickers = false,
}: TokenIconProps) {
  const [imageError, setImageError] = useState(false);
  const [workerImageError, setWorkerImageError] = useState(false);
  const [stockTickerError, setStockTickerError] = useState(false);

  // Strip common quote currencies from symbol for image lookup
  // Only removes if found at the END of the symbol (e.g., BTCUSD → BTC)
  // Ensures we don't end up with empty string
  const cleanSymbol = useMemo(() => {
    if (!symbol) return "";

    let cleaned = symbol;

    // Try removing quote currencies (in order of specificity)
    const quoteCurrencies = [/USDT$/i, /USDC$/i, /USD$/i, /EUR$/i, /GBP$/i, /JPY$/i];

    for (const pattern of quoteCurrencies) {
      const result = cleaned.replace(pattern, "");
      // Only use the result if it's not empty
      if (result.length > 0) {
        cleaned = result;
        break; // Stop after first successful removal
      }
    }

    return cleaned.toLowerCase();
  }, [symbol]);

  // Build token-img worker URL
  const getWorkerImageUrl = () => {
    if (chainId && tokenAddress && !isNativeTokenAddress(tokenAddress)) {
      return `https://token-img.b3.fun/${chainId}/${normalizeAddress(tokenAddress)}`;
    }
    // Fallback to symbol format
    return `https://token-img.b3.fun/symbol/${cleanSymbol}`;
  };

  // Use provided logoUrl first
  if (logoUrl && !imageError) {
    return (
      <img
        src={logoUrl}
        alt={symbol}
        loading="lazy"
        className={cn(`flex-shrink-0 rounded-full`, className)}
        style={{ width: `${size}px`, height: `${size}px` }}
        onError={() => setImageError(true)}
      />
    );
  }

  // Try token-img worker
  if (!workerImageError) {
    return (
      <img
        src={getWorkerImageUrl()}
        alt={symbol}
        loading="lazy"
        className={cn(`flex-shrink-0 rounded-full`, className)}
        style={{ width: `${size}px`, height: `${size}px` }}
        onError={() => setWorkerImageError(true)}
      />
    );
  }

  // Try stock ticker lookup (if enabled)
  if (enableStockTickers && !stockTickerError) {
    const logoDevToken = process.env.NEXT_PUBLIC_LOGODEV_API_KEY;

    if (logoDevToken) {
      console.log("attempting to fetch stock ticker", { cleanSymbol, symbol });
      return (
        <img
          src={`https://img.logo.dev/ticker/${cleanSymbol}?token=${logoDevToken}`}
          alt={symbol}
          loading="lazy"
          className={cn(`flex-shrink-0 rounded-full`, className)}
          style={{ width: `${size}px`, height: `${size}px` }}
          onError={() => setStockTickerError(true)}
        />
      );
    }
  }

  // Final fallback: Show first letter of symbol
  const firstLetter = (cleanSymbol || symbol || "?")[0].toUpperCase();

  return (
    <div
      className={cn(
        "bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled flex flex-shrink-0 items-center justify-center rounded-full font-semibold",
        className,
      )}
      style={{ width: `${size}px`, height: `${size}px`, fontSize: `${size / 2}px` }}
    >
      {firstLetter}
    </div>
  );
}
